import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verify } from '../services/api';
import { ShieldCheck, ArrowLeft } from 'lucide-react';

const VerifyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, firstName, lastName, email, referralCode, isLogin } = location.state || {};
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!phoneNumber) {
      navigate('/login');
    }
    inputRefs.current[0]?.focus();
  }, [phoneNumber, navigate]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...code];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newCode[index + i] = digit;
        }
      });
      setCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    } else {
      const newCode = [...code];
      newCode[index] = value.replace(/\D/g, '');
      setCode(newCode);
      
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const response = await verify(
        phoneNumber,
        fullCode,
        isLogin || false,
        firstName,
        lastName,
        email,
        referralCode
      );

      if (response.success) {
        navigate('/home');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fullCode = code.join('');
    if (fullCode.length === 6 && !loading) {
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  }, [code]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="max-w-md mx-auto flex items-center">
          <button 
            onClick={() => navigate(-1)}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} strokeWidth={2} className="text-buggy-dark" />
          </button>
          <img 
            src="/buggybusters_1.jpg" 
            alt="BuggyBusters" 
            className="h-10 w-auto"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-buggy-blue/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <ShieldCheck size={28} strokeWidth={2} className="text-buggy-blue" />
            </div>
            <h1 className="text-2xl font-bold text-buggy-dark mb-2">Enter Verification Code</h1>
            <p className="text-buggy-gray">
              We sent a 6-digit code to
            </p>
            <p className="text-buggy-dark font-semibold mt-1">{phoneNumber}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="flex justify-center gap-2 mb-6">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric" autoComplete={index === 0 ? "one-time-code" : "off"}
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-buggy-blue focus:border-transparent"
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={loading || code.join('').length !== 6}
              className="w-full bg-buggy-blue text-white font-semibold py-4 px-6 rounded-lg hover:bg-buggy-blue-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-buggy-gray">
              Didn't receive the code?{' '}
              <button 
                onClick={() => navigate(-1)}
                className="text-buggy-blue hover:text-buggy-blue-light font-semibold"
              >
                Resend
              </button>
            </p>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-buggy-gray text-center">
              The code expires in 10 minutes. If you don't see the text, 
              check your spam folder or try again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
